import { QuestionCircleOutlined } from "@ant-design/icons";
import {
	Badge,
	Button,
	Dropdown,
	Flex,
	Layout,
	Tag,
	Tooltip,
	Typography,
} from "antd";
import { useSidebar } from "app/SidebarContext";
import { ChatBar } from "components/ChatBar";

import { ColorAvatar } from "components/ColorAvatar";
import { useCompanies } from "hooks/useCompanies";
import { useHelpMenuHint } from "hooks/useHelpMenuHint";
import { useNotifications } from "hooks/useNotifications";

import { useUser } from "hooks/useUser";
import { ChevronRight } from "lucide-react";
import { NotificationCenter } from "pages/notifications/notification-center";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

const { Text } = Typography;

interface AppHeaderProps {
	/** Opens the report modal on the given tab. Owned by AppLayout, which also
	 * mounts the modal — the header and the modal are siblings, so the open
	 * state cannot live in either one. */
	onReportIssue: (kind: "bug" | "feature") => void;
}

export function AppHeader({ onReportIssue }: AppHeaderProps) {
	const { user } = useUser();
	const { pageTitle, pageOwnsHeading, pageBreadcrumb } = useSidebar();
	const [scrolled, setScrolled] = useState(false);
	const params = useParams<{ id?: string }>();
	const navigate = useNavigate();
	const location = useLocation();
	const { data: companies } = useCompanies();
	const { showHint: showHelpHint, dismissHint: dismissHelpHint } =
		useHelpMenuHint();
	// `:id` is only a company id on `/company/:id/*` routes. Other routes (e.g.
	// `/workflow/projects/:id`) use `:id` for a different entity, so resolving a
	// company from it would surface an unrelated company.
	const isCompanyRoute = location.pathname.startsWith("/company/");
	const currentCompany =
		isCompanyRoute && params.id
			? companies?.find((c) => c.id === Number(params.id))
			: null;
	const {
		notifications,
		markAsRead,
		markAllAsRead,
		dismiss,
		dismissAll,
		revalidate,
		unreadCount,
		hasMore,
		loadMore,
	} = useNotifications();

	useEffect(() => {
		const contentEl = document.querySelector(
			'[style*="overflow-y: auto"]',
		) as HTMLElement | null;
		if (!contentEl) return;

		const handleScroll = () => setScrolled(contentEl.scrollTop > 0);
		contentEl.addEventListener("scroll", handleScroll, { passive: true });
		return () => contentEl.removeEventListener("scroll", handleScroll);
	}, []);

	if (!user) return null;

	// Trimmed, because a user with neither name set interpolates to a single
	// space — truthy, so the `|| "User"` fallback below never fired and the
	// header's name slot rendered blank next to the avatar. EQU-799 §1 asks
	// for the avatar AND the name up there.
	const fullName = `${user.firstName} ${user.lastName}`.trim();

	return (
		<Layout.Header
			style={{ ...headerStyle, ...(scrolled ? scrolledStyle : {}) }}
		>
			<Flex justify="space-between" align="center" style={headerContainerStyle}>
				<Flex align="center" gap={10} style={{ minWidth: 0, flexShrink: 1 }}>
					{/*
					 * EQU-799 §1. A page deep enough to have a "where am I" answer
					 * supplies a trail, and it takes the slot the title would have
					 * used. The last segment is the current page, so it is not a link
					 * and carries aria-current; the earlier ones are plain text too,
					 * because the trail is a position marker and the tabs that would
					 * be its targets are already on screen directly below it.
					 */}
					{pageBreadcrumb.length > 0 && (
						<nav aria-label="Breadcrumb" className="app-header-breadcrumb">
							<ol className="app-header-breadcrumb-list">
								{pageBreadcrumb.map((segment, index) => (
									<li
										// A trail is short, fixed-length and rebuilt whole, so the
										// index is the stable identity here.
										key={index}
										className="app-header-breadcrumb-item"
										aria-current={
											index === pageBreadcrumb.length - 1 ? "page" : undefined
										}
									>
										{index > 0 && (
											<ChevronRight
												size={14}
												className="app-header-breadcrumb-sep"
												aria-hidden="true"
											/>
										)}
										<span className="app-header-breadcrumb-label">
											{segment}
										</span>
									</li>
								))}
							</ol>
						</nav>
					)}
					{/*
					 * A page that renders its own title and company pill suppresses
					 * both of these, so the two do not print twice 40px apart. The
					 * sidebar's company switcher still names the company, and the
					 * ChatBar slot is `flex: 1` and centers its own trigger, so it
					 * re-centers in the freed space rather than sliding to the edge.
					 */}
					{!pageOwnsHeading && pageTitle && (
						<Text
							style={{
								fontSize: 22,
								fontWeight: 500,
								color: "var(--color-text-secondary)",
								whiteSpace: "nowrap",
								overflow: "hidden",
								textOverflow: "ellipsis",
							}}
						>
							{pageTitle}
						</Text>
					)}
					{!pageOwnsHeading && currentCompany && (
						<Tag color="green" style={{ margin: 0, flexShrink: 0 }}>
							{currentCompany.alias || currentCompany.name}
						</Tag>
					)}
				</Flex>
				<ChatBar />
				<Flex gap={10} align="center" className="app-header-actions">
					{/*
					 * A Help MENU rather than a standalone "Report a bug" button.
					 * The 48px header already carries the page title, company tag,
					 * ChatBar, notifications and the account link — a fourth
					 * top-level control crowds it at medium widths. A menu also
					 * consolidates the support entry points instead of adding
					 * another one, and a bug icon would misrepresent "share an idea".
					 */}
					<Dropdown
						trigger={["click"]}
						onOpenChange={(open) => {
							// Opening the menu is the moment the button stops being
							// undiscovered, so retire the hint here rather than on a
							// specific item click — browsing the options counts.
							if (open) dismissHelpHint();
						}}
						menu={{
							items: [
								{
									key: "report-bug",
									label: "Report a problem",
									onClick: () => onReportIssue("bug"),
								},
								{
									key: "share-idea",
									label: "Share an idea",
									onClick: () => onReportIssue("feature"),
								},
								{ type: "divider" },
								{
									key: "my-requests",
									label: "My support requests",
									onClick: () => navigate("/support"),
								},
								{
									key: "help",
									label: "Help centre",
									onClick: () => navigate("/help"),
								},
							],
						}}
					>
						<Tooltip
							title={
								showHelpHint
									? "Help and feedback \u2014 new"
									: "Help and feedback"
							}
						>
							{/*
							 * A brand-green dot, not the red count the bell next to it
							 * uses: this is "there is something here you have not seen",
							 * not "something needs your attention".
							 */}
							<Badge
								dot={showHelpHint}
								color="var(--color-primary)"
								offset={[-8, 8]}
								className={showHelpHint ? "help-hint-badge" : undefined}
							>
								<Button
									type="text"
									aria-label={
										showHelpHint
											? "Help and feedback (new)"
											: "Help and feedback"
									}
									icon={<QuestionCircleOutlined style={{ fontSize: 18 }} />}
									style={helpButtonStyle}
								/>
							</Badge>
						</Tooltip>
					</Dropdown>
					<NotificationCenter
						notifications={notifications}
						onRefresh={revalidate}
						onMarkAsRead={markAsRead}
						onMarkAllAsRead={markAllAsRead}
						onDelete={dismiss}
						onDeleteAll={dismissAll}
						unreadCount={unreadCount}
						hasMore={hasMore}
						onLoadMore={loadMore}
						onSettingsClick={
							isCompanyRoute && params.id
								? () => navigate(`/company/${params.id}/settings/notifications`)
								: undefined
						}
						onView={(notification) => {
							if (!notification.link) return;
							if (/^https?:\/\//i.test(notification.link)) {
								window.open(notification.link, "_blank", "noopener,noreferrer");
							} else {
								navigate(notification.link);
							}
						}}
					/>
					<Link
						to="/account"
						aria-label="Account settings"
						style={accountLinkStyle}
					>
						<Flex gap={5} align="center" style={accountStyle}>
							<Text className="app-header-account-name">
								{fullName || "User"}
							</Text>
							<ColorAvatar size={30} name={fullName} src={user.photoUrl} />
						</Flex>
					</Link>
				</Flex>
			</Flex>
		</Layout.Header>
	);
}

const helpButtonStyle: React.CSSProperties = {
	// Matches the 44px minimum touch target the account link already uses.
	minWidth: 44,
	minHeight: 44,
	color: "var(--color-text-primary)",
};

const accountStyle: React.CSSProperties = {
	alignItems: "center",
};

const accountLinkStyle: React.CSSProperties = {
	display: "inline-flex",
	alignItems: "center",
	padding: "4px 8px",
	borderRadius: 6,
	color: "var(--color-text-primary)",
	textDecoration: "none",
	minHeight: 44,
};

const headerStyle: React.CSSProperties = {
	padding: 0,
	position: "absolute",
	left: 0,
	right: 0,
	zIndex: 10,
	background: "rgba(255, 255, 255, 0.92)",
	backdropFilter: "blur(8px)",
	boxShadow: "none",
	borderBottomWidth: 1,
	borderBottomStyle: "solid",
	borderBottomColor: "transparent",
	transition: "border-color 200ms ease, box-shadow 200ms ease",
};

const scrolledStyle: React.CSSProperties = {
	borderBottomColor: "var(--color-border)",
	boxShadow: "var(--shadow-xs)",
};

const headerContainerStyle: React.CSSProperties = {
	width: "100%",
	height: "100%",
	padding: "0 20px",
};
